//////////////////////////////////
import React, {useState, useEffect, memo} from "react";
import PropTypes from "prop-types";

import {CustomTooltip} from "../index";
import AppearancesMerger from "./AppearancesMerger";

export const Timeline = memo(
    props => {
        const {
            totalTime = 0,
            matches = [],
            featuresMatches = [],
            timelineHighlight = "",
            handleMatchClick,
            tooltip = "",
        } = props;

        const [segments, setSegments] = useState([]);

        const parseToMinutes = time => {
            if (typeof time === "number") return time;
            else if (typeof time === "string") {
                const splitStr = time.split(":");
                const minutes = +splitStr[0] * 60 * 60 + +splitStr[1] * 60 + +splitStr[2];
                return minutes;
            }
        };
        const getSegments = timestamps => {
            timestamps = mergeOverlappingTimestamps(timestamps);
            return timestamps.map(({start = "", startTime = "", end = "", duration = "", text = ""}) => {
                const startTimestamp = start || startTime;
                const endTimestamp = end;
                const startMinute = parseToMinutes(startTimestamp);
                const endMinute = end ? parseToMinutes(endTimestamp) : 0;
                const durationMinute = duration ? parseToMinutes(duration) : 0;
                const startInPercentage = `${round((startMinute * 100) / totalTime, 2)}%`;
                const endInPercentage = `${round((endMinute * 100) / totalTime, 2)}%`;
                const getWidth = () => {
                    if (durationMinute) {
                        const width = round((durationMinute * 100) / totalTime, 2);
                        return width < 1 ? "5px" : `${width}%`;
                    } else {
                        if (endMinute) {
                            const width = round(((endMinute - startMinute) * 100) / totalTime, 2);
                            return width < 1 ? "5px" : `${width}%`;
                        } else return "5px";
                    }
                };
                const width = getWidth();
                const color = startTimestamp === timelineHighlight ? "hotpink" : "";

                return {
                    start: startInPercentage,
                    end: endInPercentage,
                    width,
                    color,
                    tooltip: tooltip || text,
                    startTime: startTimestamp,
                    endTime: endTimestamp,
                    startMinute: startMinute,
                    endMinute: endMinute,
                };
            });
        };

        const mergeOverlappingTimestamps = timestamps => {
            return new AppearancesMerger().compute(timestamps);
        };

        useEffect(() => {
            const textualResults = matches?.length;
            const conceptualResults = featuresMatches?.length;

            let _segments = [];
            if (textualResults || conceptualResults) {
                if (textualResults) _segments = getSegments(matches);
                else if (conceptualResults) _segments = getSegments(featuresMatches);
                setSegments(_segments);
            } else {
                _segments = [];
                setSegments([]);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [tooltip, matches, timelineHighlight, featuresMatches]);

        return (
            <div className="card card-custom shadow px-0">
                <div className="col-12 px-0">
                    <div className="progress timeline_bar">
                        {segments &&
                            segments.map(({start = "0%", width = "0%", color = "", startTime = "", tooltip}, i) => (
                                <CustomTooltip key={i} config={{text: tooltip, placement: "top"}}>
                                    <div
                                        key={i}
                                        className="progress-bar timeline_segment"
                                        onClick={() => handleMatchClick(startTime)}
                                        style={{
                                            width,
                                            backgroundColor: color || "#456fb3",
                                            left: start,
                                        }}
                                    />
                                </CustomTooltip>
                            ))}
                    </div>
                </div>
            </div>
        );
    },
    (prevProps, nextProps) =>
        prevProps.matches === nextProps.matches &&
        prevProps.timelineHighlight === nextProps.timelineHighlight &&
        prevProps.featuresMatches === nextProps.featuresMatches &&
        prevProps.tooltip === nextProps.tooltip
);

Timeline.propTypes = {
    totalTime: PropTypes.number,
    matches: PropTypes.arrayOf(PropTypes.object),
    featuresMatches: PropTypes.arrayOf(PropTypes.object),
    timelineHighlight: PropTypes.string,
    handleMatchClick: PropTypes.func,
};

function round(n, d) {
    return Math.round(n * Math.pow(10, d)) / Math.pow(10, d);
}
